import {
  CreateGUFNewLicenceTask,
  EditGUFNewLicenceTask,
  QualityRegimeOptions,
} from "@nens/brostar/uploadapi-client";

import { BrostarUiSchema } from "@/libs/brostar-form";

import { createGufUiSchemaLibrary } from "./guf-ui-schema-library";

export function useGufNewLicenceUiSchema(
  qualityRegimeValue: QualityRegimeOptions | undefined
): BrostarUiSchema<CreateGUFNewLicenceTask | EditGUFNewLicenceTask> {
  const {
    projectNumber,
    qualityRegime,
    deliveryAccountableParty,
    broId,
    correctionReason,
    startTime,
    identificationLicence,
    legalType,
    primaryUsageType,
    secondaryUsageTypes,
    humanConsumption,
    licensedInOut,
    maximumPerHour,
    maximumPerDay,
    maximumPerMonth,
    maximumPerQuarter,
    maximumPerYear,
    designInstallationId,
    designInstallationPos,
    installationFunction,
    wellPos,
    energyCharacteristics,
    energyCold,
    energyWarm,
    maximumInfiltrationTemperatureWarm,
    averageInfiltrationTemperatureCold,
    averageInfiltrationTemperatureWarm,
    powerCold,
    powerWarm,
    power,
    averageColdWater,
    averageWarmWater,
    maximumYearQuantityCold,
    maximumYearQuantityWarm,
    designWellId,
    wellFunctions,
    height,
    geometryPubliclyAvailable,
    maximumWellDepth,
    maximumWellDepthPubliclyAvailable,
    maximumWellCapacity,
    relativeTemperature,
    screenType,
    designScreenTop,
    designScreenBottom,
    designScreenGeometry,
    designLoopId,
    designLoopPos,
    loopType,
    startDate,
    endDate,
    geometryType,
    designSurfaceInfiltrationId,
    designSurfaceInfiltrationPos,
  } = createGufUiSchemaLibrary(qualityRegimeValue);

  const uiSchema: BrostarUiSchema<CreateGUFNewLicenceTask | EditGUFNewLicenceTask> = {
    "ui:order": ["metadata", "sourcedocumentData"],
    metadata: {
      "ui:title": "Metadata",
      projectNumber,
      qualityRegime,
      deliveryAccountableParty,
      broId,
      correctionReason,
    },
    sourcedocumentData: {
      "ui:title": "Brondocumentgegevens",
      "ui:order": [
        "startTime",
        "identificationLicence",
        "legalType",
        "primaryUsageType",
        "secondaryUsageTypes",
        "humanConsumption",
        "licensedQuantities",
        "designInstallations",
      ],
      startTime,
      identificationLicence,
      legalType,
      primaryUsageType,
      secondaryUsageTypes,
      humanConsumption,
      licensedQuantities: {
        "ui:options": { labelField: "licensedInOut" },
        items: {
          "ui:order": [
            "licensedInOut",
            "maximumPerHour",
            "maximumPerDay",
            "maximumPerMonth",
            "maximumPerQuarter",
            "maximumPerYear",
          ],
          licensedInOut,
          maximumPerHour,
          maximumPerDay,
          maximumPerMonth,
          maximumPerQuarter,
          maximumPerYear,
        },
      },
      designInstallations: {
        "ui:options": { labelField: "designInstallationId" },
        items: {
          "ui:order": [
            "designInstallationId",
            "installationFunction",
            "designInstallationPos",
            "energyCharacteristics",
            "licensedQuantities",
            "designLoops",
            "designSurfaceInfiltrations",
            "designWells",
          ],
          designInstallationId,
          installationFunction,
          designInstallationPos,
          energyCharacteristics: {
            ...energyCharacteristics,
            "ui:order": [
              "energyCold",
              "energyWarm",
              "maximumInfiltrationTemperatureWarm",
              "averageInfiltrationTemperatureCold",
              "averageInfiltrationTemperatureWarm",
              "powerCold",
              "powerWarm",
              "power",
              "averageColdWater",
              "averageWarmWater",
              "maximumYearQuantityCold",
              "maximumYearQuantityWarm",
            ],
            energyCold,
            energyWarm,
            maximumInfiltrationTemperatureWarm,
            averageInfiltrationTemperatureCold,
            averageInfiltrationTemperatureWarm,
            powerCold,
            powerWarm,
            power,
            averageColdWater,
            averageWarmWater,
            maximumYearQuantityCold,
            maximumYearQuantityWarm,
          },
          licensedQuantities: {
            "ui:options": { labelField: "licensedInOut" },
            items: {
              "ui:order": [
                "licensedInOut",
                "maximumPerHour",
                "maximumPerDay",
                "maximumPerMonth",
                "maximumPerQuarter",
                "maximumPerYear",
              ],
              licensedInOut,
              maximumPerHour,
              maximumPerDay,
              maximumPerMonth,
              maximumPerQuarter,
              maximumPerYear,
            },
          },
          designLoops: {
            "ui:options": { labelField: "designLoopId" },
            items: {
              "ui:order": [
                "designLoopId",
                "designLoopPos",
                "loopType",
                "startDate",
                "endDate",
                "geometryType",
              ],
              designLoopId,
              designLoopPos,
              loopType,
              startDate,
              endDate,
              geometryType,
            },
          },
          designSurfaceInfiltrations: {
            "ui:options": { labelField: "designSurfaceInfiltrationId" },
            items: {
              "ui:order": ["designSurfaceInfiltrationId", "designSurfaceInfiltrationPos"],
              designSurfaceInfiltrationId,
              designSurfaceInfiltrationPos,
            },
          },
          designWells: {
            "ui:options": { labelField: "designWellId" },
            items: {
              "ui:order": [
                "designWellId",
                "wellFunctions",
                "installationFunction",
                "height",
                "wellPos",
                "geometryPubliclyAvailable",
                "maximumWellDepth",
                "maximumWellDepthPubliclyAvailable",
                "maximumWellCapacity",
                "relativeTemperature",
                "designScreen",
              ],
              designWellId,
              installationFunction,
              wellFunctions,
              height,
              wellPos,
              geometryPubliclyAvailable,
              maximumWellDepth,
              maximumWellDepthPubliclyAvailable,
              maximumWellCapacity,
              relativeTemperature,
              designScreen: {
                "ui:order": [
                  "screenType",
                  "designScreenTop",
                  "designScreenBottom",
                  "designScreenGeometry",
                  "geometryPubliclyAvailable",
                ],
                screenType,
                designScreenTop,
                designScreenBottom,
                designScreenGeometry,
                geometryPubliclyAvailable,
              },
            },
          },
        },
      },
    },
  };

  return uiSchema;
}
